import React, { useEffect, useState } from 'react';
import { Tooltip, Button } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';

// Cross-browser wrappers around the Fullscreen API.
const requestFullscreen = (el) => {
  if (el.requestFullscreen) return el.requestFullscreen();
  if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
  if (el.msRequestFullscreen) return el.msRequestFullscreen();
};

const exitFullscreen = () => {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  if (document.msExitFullscreen) return document.msExitFullscreen();
};

const getFullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;

export default function FullscreenToggle() {
  const { isDark } = useTheme();
  const [isFullscreen, setIsFullscreen] = useState(!!getFullscreenElement());

  // Keep local state in sync with actual fullscreen changes (incl. Esc key / F11).
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!getFullscreenElement());
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    document.addEventListener('msfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      document.removeEventListener('msfullscreenchange', onChange);
    };
  }, []);

  const bgColor = isDark ? defaultColors.grey[700] : defaultColors.grey[100];
  const iconColor = isDark ? defaultColors.grey[100] : defaultColors.grey[700];

  const toggle = () => {
    if (getFullscreenElement()) {
      exitFullscreen();
    } else {
      requestFullscreen(document.documentElement);
    }
  };

  return (
    <Tooltip title={isFullscreen ? 'Exit full screen' : 'Full screen'} placement="bottom" zIndex={9999}>
      <Button
        type="text"
        shape="circle"
        onClick={toggle}
        icon={
          isFullscreen ? (
            <FullscreenExitOutlined style={{ fontSize: 18, color: iconColor }} />
          ) : (
            <FullscreenOutlined style={{ fontSize: 18, color: iconColor }} />
          )
        }
        aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
        style={{
          backgroundColor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
        className="transition-all duration-300"
      />
    </Tooltip>
  );
}
